// Sign-in screen: branded header (the ICM PAY badge + app name), password / email-OTP
// tabs (last-used tab and username remembered per device), an in-field password toggle,
// and an OTP step with a resend countdown. Google sign-in is still TODO
// (port GoogleSignInButton + /signin/apiAuthGoogle in a later phase).

import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, KeyRound, Loader2, LogIn, Mail, ShieldCheck } from "lucide-react";

import { ApiError, requestOtp, verifyOtp, type LoginResult } from "@/lib/api";
import {
  clearSsoBounce,
  getSsoToken,
  NoAppAccountError,
  redeemSsoCode,
  redirectToSso,
  signInWithSsoRedirect,
  SSO_ENABLED,
  ssoSignedOut,
} from "@/lib/sso-client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GcipSignIn } from "@/components/gcip-sign-in";

// Per-device conveniences; storage can be unavailable (private mode) — never required.
function remember(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}
function recall(key: string): string {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}
// Login fields: 48px touch targets, 16px text (comfortable + iOS-zoom-safe), clearer border.
const FIELD = "h-12 text-base border-muted-foreground/30";

const TAB_KEY = "icm_donation_login_tab";
const USER_KEY = "icm_donation_login_user";

export default function LoginPage() {
  const { signIn, signInWithToken, token, ready } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const target = next.startsWith("/") ? next : "/";
  const [tab, setTab] = useState(() => (recall(TAB_KEY) === "otp" ? "otp" : "password"));
  // RGSEVA SSO: silently redeem an existing org-wide session before showing the form.
  const [ssoChecking, setSsoChecking] = useState(SSO_ENABLED);
  const [ssoBusy, setSsoBusy] = useState(false);
  const ssoProbed = useRef(false);

  // Already signed in → skip the login screen.
  useEffect(() => {
    if (ready && token) navigate(target, { replace: true });
  }, [ready, token, target, navigate]);

  /**
   * Enter the app after a successful sign-in. A full page load, not navigate(): the token has
   * just been written to React state and localStorage, and a client-side navigation can render
   * the destination before the auth context holds it — the guard then bounces back here and the
   * user has to refresh by hand. A real load reads the token back from localStorage.
   */
  function done() {
    window.location.replace(target);
  }
  function fail(e: unknown) {
    toast.error(e instanceof ApiError ? e.message : "Sign in failed. Please try again.");
  }

  // Silent SSO attempt on mount (once): signed in at login.rgseva.com → straight in.
  useEffect(() => {
    if (!ready) return;
    if (!SSO_ENABLED || token) {
      setSsoChecking(false);
      return;
    }
    if (ssoProbed.current) return; // StrictMode double-mount: probe only once
    ssoProbed.current = true;
    // Signed out on purpose → no silent probe and no bounce, or the user is signed straight back
    // into the app they just left. A one-time code on the URL is the return leg of an explicit
    // sign-in, so that is still redeemed.
    if (ssoSignedOut() && !window.location.search.includes("sso_code")) {
      setSsoChecking(false);
      return;
    }
    // A one-time code on the URL means we are the return leg of a redirect to
    // login.rgseva.com, so redeem that first: it is the only path that works when this
    // app is not on *.rgseva.com and the SSO cookie cannot reach us.
    redeemSsoCode()
      .then((viaCode) => (viaCode ? viaCode : getSsoToken()))
      .then((r) => {
        if (r) {
          clearSsoBounce();
          signInWithToken(r);
          done();
          return;
        }
        // No org session. With SSO the only way in, a page whose only control is a
        // "Sign in with RGSEVA ID" button is just an extra click — send the browser
        // there instead. redirectToSso() refuses to bounce twice, so someone with no
        // account in THIS app still lands on a real page.
        if (redirectToSso()) return;
      })
      .catch((e) => {
        // Org session exists but this app has no account — say so once; other
        // failures stay silent (the regular form is right below).
        if (e instanceof NoAppAccountError) toast.error(e.message);
      })
      .finally(() => setSsoChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, token]);

  // The "Sign in with ICM ID" button → a full-page redirect to login.rgseva.com, the same way the
  // Grails app does it. It returns here with a one-time code, redeemed by the effect above.
  // (An explicit click also clears the "signed out on purpose" marker.)
  function ssoSignIn() {
    setSsoBusy(true);
    if (!signInWithSsoRedirect()) setSsoBusy(false);
  }

  if (ready && token) return null;

  return (
    <div className="min-h-viewport pt-login-anchor mx-auto flex max-w-sm flex-col gap-6 p-6">
      {/* Brand header — mirrors the home-screen icon */}
      <div className="flex flex-col items-center gap-3 text-center">
        <div
          aria-hidden="true"
          className="grid size-16 place-items-center rounded-full border-4 border-orange-100 bg-gradient-to-b from-orange-500 to-orange-600 leading-none text-white shadow-lg shadow-orange-600/20"
        >
          <div>
            <div className="text-[17px] font-bold tracking-tight">ICM</div>
            <div className="text-[7.5px] font-bold tracking-[0.22em] text-orange-100">PAY</div>
          </div>
        </div>
        <div>
          <h1 className="text-xl font-bold">RGSEVA Donations</h1>
          <p className="text-sm text-muted-foreground">Record and track donations</p>
        </div>
      </div>

      {ssoChecking ? (
        <div className="flex flex-col items-center gap-2 py-8 text-muted-foreground">
          <Loader2 className="size-6 animate-spin" />
          <p className="text-sm">Checking your ICM session…</p>
        </div>
      ) : (
      <Card className="bg-transparent py-0 shadow-none ring-0 sm:bg-card sm:py-6 sm:shadow-sm sm:ring-1">
        <CardContent className="px-0 pt-2 sm:px-6 sm:pt-4">
          {SSO_ENABLED && (
            <>
              <Button
                type="button"
                className="mb-4 h-12 w-full text-base"
                disabled={ssoBusy}
                onClick={ssoSignIn}
              >
                {ssoBusy ? <Loader2 className="animate-spin" /> : <ShieldCheck className="size-4" />}
                {ssoBusy ? "Waiting for ICM…" : "Sign in with ICM ID"}
              </Button>
              <div className="mb-4 flex items-center gap-3" aria-hidden="true">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">or</span>
                <div className="h-px flex-1 bg-border" />
              </div>
            </>
          )}
          {/* Local sign-in is offered ONLY when the organization's front door is
              unavailable. Hiding it while SSO is on is not just cosmetic: it keeps every
              session going through login.rgseva.com, so revocation and the device list
              actually mean something. Gated on SSO_ENABLED rather than removed, so a
              misconfigured or unreachable SSO service cannot lock anyone out. */}
          {!SSO_ENABLED && (
            <>
          <Tabs
            value={tab}
            onValueChange={(v) => {
              setTab(v);
              remember(TAB_KEY, v);
            }}
          >
            <TabsList className="w-full">
              <TabsTrigger value="password" className="flex-1">
                Password
              </TabsTrigger>
              <TabsTrigger value="otp" className="flex-1">
                Email OTP
              </TabsTrigger>
            </TabsList>

            <TabsContent value="password">
              <PasswordForm onSignIn={signIn} onDone={done} onFail={fail} />
            </TabsContent>

            <TabsContent value="otp">
              <OtpForm onDone={done} onFail={fail} onToken={signInWithToken} />
            </TabsContent>
          </Tabs>

          <div className="mt-4 flex items-center gap-3" aria-hidden="true">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>
          <div className="mt-4">
            <GcipSignIn onToken={signInWithToken} onDone={done} />
          </div>
            </>
          )}
        </CardContent>
      </Card>
      )}

      <p className="text-center text-xs text-muted-foreground">
        Same account as the main system ·{" "}
        <a href="/install.html" className="underline underline-offset-2 hover:text-foreground">
          How to install this app
        </a>
      </p>
    </div>
  );
}

function PasswordForm({
  onSignIn,
  onDone,
  onFail,
}: {
  onSignIn: (u: string, p: string) => Promise<void>;
  onDone: () => void;
  onFail: (e: unknown) => void;
}) {
  const [username, setUsername] = useState(() => recall(USER_KEY));
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await onSignIn(username.trim(), password);
      remember(USER_KEY, username.trim());
      onDone();
    } catch (err) {
      onFail(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 pt-1">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="username">Email or mobile number</Label>
        <Input
          id="username"
          name="username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
          autoFocus={!username}
          placeholder="you@example.com"
          className={FIELD}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="password">Password</Label>
        <div className="relative">
          <Input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            autoFocus={!!username}
            className={`${FIELD} pr-12`}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="absolute top-1/2 right-1 -translate-y-1/2 text-muted-foreground"
            aria-label={showPassword ? "Hide password" : "Show password"}
            onClick={() => setShowPassword((s) => !s)}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </Button>
        </div>
      </div>
      <Button type="submit" className="mt-1 h-12 w-full text-base" disabled={busy || !username.trim() || !password}>
        <LogIn className="size-4" /> {busy ? "Signing in…" : "Sign in"}
      </Button>
    </form>
  );
}

const RESEND_SECONDS = 30;

function OtpForm({
  onDone,
  onFail,
  onToken,
}: {
  onDone: () => void;
  onFail: (e: unknown) => void;
  onToken: (result: LoginResult) => void;
}) {
  const [step, setStep] = useState<"email" | "code">("email");
  const [email, setEmail] = useState(() => {
    const u = recall(USER_KEY);
    return u.includes("@") ? u : "";
  });
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  function startCooldown() {
    setCooldown(RESEND_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1 && timerRef.current) clearInterval(timerRef.current);
        return Math.max(0, c - 1);
      });
    }, 1000);
  }
  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  async function send(e?: React.FormEvent) {
    e?.preventDefault();
    setBusy(true);
    try {
      await requestOtp(email.trim());
      remember(USER_KEY, email.trim());
      toast.success("Code sent — check your email");
      setStep("code");
      setOtp("");
      startCooldown();
    } catch (err) {
      onFail(err);
    } finally {
      setBusy(false);
    }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await verifyOtp(email.trim(), otp.trim());
      onToken(r);
      onDone();
    } catch (err) {
      onFail(err);
    } finally {
      setBusy(false);
    }
  }

  if (step === "email") {
    return (
      <form onSubmit={send} className="flex flex-col gap-3 pt-1">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="otp-email">Email</Label>
          <Input
            id="otp-email"
            name="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoFocus
            className={FIELD}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          We'll email you a one-time code — no password needed.
        </p>
        <Button type="submit" className="mt-1 h-12 w-full text-base" disabled={busy || !email.trim()}>
          <Mail className="size-4" /> {busy ? "Sending…" : "Send code"}
        </Button>
      </form>
    );
  }

  return (
    <form onSubmit={verify} className="flex flex-col gap-3 pt-1">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="otp-code">One-time code</Label>
        <p className="text-xs text-muted-foreground">
          Sent to <b className="text-foreground">{email}</b>
        </p>
        <Input
          id="otp-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="••••••"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          autoFocus
          maxLength={8}
          className={`${FIELD} h-14 text-center text-xl font-semibold tracking-[0.5em]`}
        />
      </div>
      <Button type="submit" className="mt-1 h-12 w-full text-base" disabled={busy || otp.length < 4}>
        <KeyRound className="size-4" /> {busy ? "Verifying…" : "Verify & sign in"}
      </Button>
      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="link"
          size="xs"
          className="gap-1 px-0 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => {
            setStep("email");
            setOtp("");
          }}
        >
          <ArrowLeft className="size-3" /> Change email
        </Button>
        <Button
          type="button"
          variant="link"
          size="xs"
          className="px-0 text-xs text-muted-foreground hover:text-foreground"
          disabled={busy || cooldown > 0}
          onClick={() => send()}
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </Button>
      </div>
    </form>
  );
}
